import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { ReactKeycloakProvider } from '@react-keycloak/web'
import { AuthClientEvent, AuthClientError } from '@react-keycloak/core'
import keycloak from '../keycloak'
import KeycloakLoading from '@/components/ui/loaders/KeycloakLoading'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { keycloakAuthSuccess, refreshToken, setOfflineAuth, setOnlineMode } from '@/redux/reducers/auth.reducer'
import { selectAppSettings } from '@/redux/reducers/app.reducer'
import { OFFLINE_AUTH_KEY, OFFLINE_USER_KEY, OFFLINE_TOKEN_KEY, OfflineAuthData } from '@/constants/offline-auth'

interface OfflineAuthContextType {
  isOfflineAuth: boolean
  canUseOfflineAuth: boolean
}

const OfflineAuthContext = createContext<OfflineAuthContextType>({
  isOfflineAuth: false,
  canUseOfflineAuth: false,
})

export const useOfflineAuth = () => useContext(OfflineAuthContext)

// Mock Keycloak context for offline mode
const MockKeycloakContext = createContext<any>({
  keycloak: {
    authenticated: false,
    token: null,
    refreshToken: null,
    idToken: null,
    loadUserInfo: () => Promise.resolve({}),
  },
})

const MockKeycloakProvider = ({ children }: { children: React.ReactNode }) => {
  const mockKeycloak = {
    authenticated: false,
    token: null,
    refreshToken: null,
    idToken: null,
    loadUserInfo: () => Promise.resolve({}),
  }

  return <MockKeycloakContext.Provider value={{ keycloak: mockKeycloak }}>{children}</MockKeycloakContext.Provider>
}

// Online handler: uses real Keycloak hook
function OfflineAuthHandlerOnline({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const { keycloak: kc } = useKeycloak()
  const [isOfflineAuth, setIsOfflineAuth] = useState(false)
  const [canUseOfflineAuth, setCanUseOfflineAuth] = useState(false)
  const { autoSwitchOfflineMode } = useAppSelector(selectAppSettings)

  const checkOfflineAuth = useCallback(() => {
    // Do not enable offline auth while online; only prepare flags when offline
    if (navigator.onLine) {
      return false
    }

    // Check if auto-switch to offline mode is enabled
    if (!autoSwitchOfflineMode) {
      return false
    }

    const offlineAuthData = localStorage.getItem(OFFLINE_AUTH_KEY)
    const offlineUserData = localStorage.getItem(OFFLINE_USER_KEY)
    const offlineTokenData = localStorage.getItem(OFFLINE_TOKEN_KEY)

    const hasOfflineAuth = !!offlineAuthData && !!offlineUserData && !!offlineTokenData

    if (!hasOfflineAuth) {
      return false
    }

    try {
      const authData = JSON.parse(offlineAuthData)
      const userData = JSON.parse(offlineUserData)
      const tokenData = JSON.parse(offlineTokenData)

      // Check if offline auth is still valid (48 hours)
      const lastAuth = new Date(authData.lastOnlineAuth)
      const hoursSinceAuth = (Date.now() - lastAuth.getTime()) / (1000 * 60 * 60)

      if (hoursSinceAuth < 48) {
        // Dispatch offline authentication
        dispatch(
          setOfflineAuth({
            user: userData,
            token: tokenData.token,
            isOffline: true,
          })
        )

        return true
      } else {
        // Clear expired offline data
        localStorage.removeItem(OFFLINE_AUTH_KEY)
        localStorage.removeItem(OFFLINE_USER_KEY)
        localStorage.removeItem(OFFLINE_TOKEN_KEY)
        return false
      }
    } catch (error) {
      console.error('Error parsing offline auth data:', error)
      return false
    }
  }, [dispatch, autoSwitchOfflineMode])

  useEffect(() => {
    const checkOfflineAuthResult = checkOfflineAuth()

    // Listen for online/offline changes
    const handleOnline = () => {
      dispatch(setOnlineMode())
      setIsOfflineAuth(false)
    }

    const handleOffline = () => {
      // Only auto-switch to offline mode if the setting is enabled
      if (autoSwitchOfflineMode) {
        checkOfflineAuth()
        setIsOfflineAuth(true)
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isOfflineAuth, autoSwitchOfflineMode, checkOfflineAuth, dispatch])

  // Save auth data when user successfully authenticates online (only if Keycloak is available)
  useEffect(() => {
    if (kc?.authenticated && !isOfflineAuth && navigator.onLine) {
      const saveOfflineAuth = async () => {
        try {
          // Get user info from Keycloak
          const userInfo = await kc.loadUserInfo()

          const offlineAuthData: OfflineAuthData = {
            lastOnlineAuth: new Date().toISOString(),
            userInfo,
            tokenInfo: {
              token: kc.token,
              refreshToken: kc.refreshToken,
              idToken: kc.idToken,
            },
          }

          localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(offlineAuthData))
          localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(userInfo))
          localStorage.setItem(OFFLINE_TOKEN_KEY, JSON.stringify(offlineAuthData.tokenInfo))
        } catch (error) {
          console.error('Failed to save offline auth data:', error)
        }
      }

      saveOfflineAuth()
    }
  }, [kc?.authenticated, isOfflineAuth, kc])

  return <OfflineAuthContext.Provider value={{ isOfflineAuth, canUseOfflineAuth }}>{children}</OfflineAuthContext.Provider>
}

// Offline handler: uses mock keycloak context only
function OfflineAuthHandlerOffline({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const { keycloak: kc } = useContext(MockKeycloakContext)
  const [isOfflineAuth, setIsOfflineAuth] = useState(true)
  const [canUseOfflineAuth, setCanUseOfflineAuth] = useState(false)
  const { autoSwitchOfflineMode } = useAppSelector(selectAppSettings)

  const checkOfflineAuth = useCallback(() => {
    const offlineAuthData = localStorage.getItem(OFFLINE_AUTH_KEY)
    const offlineUserData = localStorage.getItem(OFFLINE_USER_KEY)
    const offlineTokenData = localStorage.getItem(OFFLINE_TOKEN_KEY)

    const hasOfflineAuth = !!offlineAuthData && !!offlineUserData && !!offlineTokenData

    if (!hasOfflineAuth) {
      return false
    }

    try {
      const authData = JSON.parse(offlineAuthData)
      const userData = JSON.parse(offlineUserData)
      const tokenData = JSON.parse(offlineTokenData)

      const lastAuth = new Date(authData.lastOnlineAuth)
      const hoursSinceAuth = (Date.now() - lastAuth.getTime()) / (1000 * 60 * 60)

      if (hoursSinceAuth < 48) {
        dispatch(
          setOfflineAuth({
            user: userData,
            token: tokenData.token,
            isOffline: true,
          })
        )
        return true
      } else {
        localStorage.removeItem(OFFLINE_AUTH_KEY)
        localStorage.removeItem(OFFLINE_USER_KEY)
        localStorage.removeItem(OFFLINE_TOKEN_KEY)
        return false
      }
    } catch (error) {
      console.error('Error parsing offline auth data:', error)
      return false
    }
  }, [dispatch])

  useEffect(() => {
    checkOfflineAuth()

    const handleOnline = () => {
      dispatch(setOnlineMode())
      setIsOfflineAuth(false)
    }

    const handleOffline = () => {
      // Only auto-switch to offline mode if the setting is enabled
      if (autoSwitchOfflineMode) {
        checkOfflineAuth()
        setIsOfflineAuth(true)
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [dispatch, checkOfflineAuth, autoSwitchOfflineMode])

  // No saving of offline auth in offline handler

  return <OfflineAuthContext.Provider value={{ isOfflineAuth, canUseOfflineAuth }}>{children}</OfflineAuthContext.Provider>
}

// Main provider component
export function OfflineAuthProvider({ children }: { children: React.ReactNode }) {
  const [shouldBypassKeycloak, setShouldBypassKeycloak] = useState(false)
  const dispatch = useAppDispatch()
  const { autoSwitchOfflineMode } = useAppSelector(selectAppSettings)

  useEffect(() => {
    const checkAuthState = () => {
      const isOffline = !navigator.onLine
      const hasOfflineAuth = localStorage.getItem(OFFLINE_AUTH_KEY)

      // Only switch to offline mode if the setting is enabled
      if (isOffline && hasOfflineAuth && autoSwitchOfflineMode) {
        if (!shouldBypassKeycloak) {
          setShouldBypassKeycloak(true)
          // Load offline auth immediately
          try {
            const userData = JSON.parse(localStorage.getItem(OFFLINE_USER_KEY) || '{}')
            const tokenData = JSON.parse(localStorage.getItem(OFFLINE_TOKEN_KEY) || '{}')
            dispatch(setOfflineAuth({ user: userData, token: tokenData, isOffline: true }))
          } catch (error) {
            console.error('Failed to load offline auth on startup:', error)
            setShouldBypassKeycloak(false) // Fallback to online mode
          }
        }
      } else {
        // Always clear offline mode when back online
        if (shouldBypassKeycloak) {
          dispatch(setOnlineMode())
          setShouldBypassKeycloak(false)
        }
      }
    }

    checkAuthState()

    window.addEventListener('online', checkAuthState)
    window.addEventListener('offline', checkAuthState)

    return () => {
      window.removeEventListener('online', checkAuthState)
      window.removeEventListener('offline', checkAuthState)
    }
  }, [dispatch, shouldBypassKeycloak, autoSwitchOfflineMode])

  const keycloakEventHandler = (event: AuthClientEvent, error?: AuthClientError) => {
    switch (event) {
      case 'onAuthSuccess':
        dispatch(keycloakAuthSuccess())
        break
      case 'onAuthRefreshSuccess':
        dispatch(refreshToken())
        break
      case 'onAuthRefreshError':
        keycloak.clearToken()
        break
      default:
        break
    }
  }

  // If we should bypass Keycloak, render children directly with offline auth
  if (shouldBypassKeycloak) {
    return (
      <MockKeycloakProvider>
        <OfflineAuthHandlerOffline>{children}</OfflineAuthHandlerOffline>
      </MockKeycloakProvider>
    )
  }

  // Normal Keycloak flow
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{
        checkLoginIframe: false,
        onLoad: 'check-sso',
        scope: 'openid',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      }}
      LoadingComponent={<KeycloakLoading timeoutAfter={10} />}
      onEvent={keycloakEventHandler}
    >
      <OfflineAuthHandlerOnline>{children}</OfflineAuthHandlerOnline>
    </ReactKeycloakProvider>
  )
}
